import type * as React from 'react';
import { DonutChart } from '@/components/shared/DonutChart';
import { cn } from '@/lib/utils/cn';

interface CompletionPanelProps {
  /** Card heading, e.g. "Profile Completion" / "Completion". */
  title: string;
  /** Completion percentage 0..100 shown in the donut. Omit to render the
   *  card without the progress donut — a plain content menu card. */
  pct?: number;
  /** Already-localized line under the donut, e.g. "21 of 57 fields filled". */
  fillLabel?: React.ReactNode;
  /** Optional override for the donut's sub-label (defaults to "complete"). */
  completeLabel?: string;
  /** The navigable menu — a `SectionTree`, questionnaire outline, or any
   *  section/subsection list. */
  children: React.ReactNode;
  className?: string;
}

/**
 * Reusable completion sidebar card: a progress donut + fill count on top,
 * then a navigable section/subsection menu below — all on one card.
 * Shared by the network company profile and the questionnaire review
 * page so the "progress + content menu" affordance looks identical.
 * Pages with nothing to measure (e.g. the product overview) omit `pct`
 * and get the same card with only the menu.
 */
export function CompletionPanel({
  title,
  pct,
  fillLabel,
  completeLabel,
  children,
  className,
}: CompletionPanelProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-6 rounded-lg border border-border bg-card p-6',
        className,
      )}
    >
      <span className='text-sm font-semibold text-foreground'>{title}</span>

      {pct !== undefined && (
        <div className='flex flex-col items-center gap-2'>
          <DonutChart
            pct={pct}
            {...(completeLabel !== undefined ? { label: completeLabel } : {})}
          />
          <span className='text-xs text-muted-foreground tabular-nums'>
            {fillLabel}
          </span>
        </div>
      )}

      {children}
    </div>
  );
}
